import * as React from 'react';
import MaterialTable from 'material-table';
import LinearProgress from '@material-ui/core/LinearProgress';
import ImportFileDialog from '../components/import-file-dialog.component';
import ImportCsvFileDialog from '../components/import-csv-file-dialog.component';
import AddImportTypeDialog from '../components/add-import-type-dialog.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    getImportTypes,
    clearImportType,
    refreshTotals,
    setAddImportTypeModal,
    addImportTypeModal$,
    getImportLines,
    importTypes$,
} from '../services/import-types.service';
import { ImportType } from '../interfaces/import-type';
import { List } from 'linqts';
import { ConfirmationDialog } from '../../shared/components/confirmation.dialog.component';

interface Props { }
interface State {
    showProgress: boolean;
    importTypes: ImportType[];
    selectedImportType?: ImportType;
    showImportFileDialog: boolean;
    showClearConfirmationDialog: boolean;
    showAddImportTypeDialog: boolean;
}

const ReadOnlyImportTypeIds = new List<number>([1]);
const CsvImportTypeIds = new List<number>([1, 2, 3]);

export class ImportsGrid extends React.Component<Props, State> {
    private destroyed$ = new Subject<void>();
    private columns: any = [{
        title: 'Nom',
        field: 'name',
        editable: 'never',
    }, {
        title: 'Type de lignes actualisées',
        field: 'lineType',
        editable: 'never',
    }, {
        title: 'Administrateur',
        field: 'updatedBy',
        editable: 'never',
    }, {
        title: 'Total d’imports',
        field: 'totalFeatures',
        editable: 'never',
    }, {
        title: 'Date MAJ',
        field: 'updatedAt',
        editable: 'never',
        // material-table ne formate que de vrais objets Date : sans ce render, la date ISO 8601
        // renvoyée par l'api s'afficherait brute
        render: (rowData: ImportType) => rowData.updatedAt
            ? new Date(rowData.updatedAt).toLocaleString('fr-FR')
            : '—',
    }];

    constructor(props: Props) {
        super(props);

        this.state = {
            showProgress: false,
            importTypes: [] as ImportType[],
            selectedImportType: undefined,
            showImportFileDialog: false,
            showClearConfirmationDialog: false,
            showAddImportTypeDialog: false,
        } as State;
    }

    public render() {
        const {
            showProgress,
            importTypes,
            showImportFileDialog,
            selectedImportType,
            showClearConfirmationDialog,
            showAddImportTypeDialog,
        } = this.state;

        return (
            <div className='imports grid'>
                { showProgress && <LinearProgress /> }
                { showImportFileDialog
                    && selectedImportType
                    && CsvImportTypeIds.Contains(selectedImportType.id)
                    ? <ImportCsvFileDialog
                        open={showImportFileDialog}
                        importType={selectedImportType!}
                        onClose={ this.handleImportFileDialogClose } />
                    : <ImportFileDialog
                        open={showImportFileDialog}
                        importType={selectedImportType!}
                        onClose={ this.handleImportFileDialogClose } /> }
                { showAddImportTypeDialog && <AddImportTypeDialog /> }
                { showClearConfirmationDialog
                    && <ConfirmationDialog
                        title='Notification'
                        message='Etes-vous sûr de supprimer les données ?'
                        onClose={ this.handleOnClearConfirmed } />}
                <MaterialTable columns={this.columns}
                    title={'Lignes électriques'}
                    data={importTypes}
                    localization={{
                        toolbar: { searchPlaceholder: 'Recherche' },
                        pagination: {
                            labelRowsSelect: 'lignes',
                            labelDisplayedRows: ' {from}-{to} sur {count}',
                         },
                    }}
                    actions={[
                        {
                          icon: 'add',
                          tooltip: 'Ajouter un type d\'import.',
                          isFreeAction: true,
                          onClick: this.handleOnAdd,
                        },
                        {
                          icon: 'refresh',
                          tooltip: 'Recalculer les totaux depuis la base.',
                          isFreeAction: true,
                          onClick: this.handleOnRefreshTotals,
                        },
                        {
                          icon: 'map',
                          tooltip: 'Afficher les lignes de cet import sur la carte.',
                          onClick: this.handleOnShowOnMap,
                        },
                        {
                          icon: 'cloud_upload',
                          tooltip: 'Télécharger le fichier GeoJson.',
                          onClick: this.handleOnImport,
                        },
                        {
                          icon: 'highlight_off',
                          tooltip: 'Effacer les lignes.',
                          onClick: this.handleOnClear,
                        },
                    ]}
                    />
            </div>
        );
    }

    public componentDidMount() {
        this.subscribeToImportTypes();
        this.subscribeToAddImportTypeModal();

        getImportTypes();
    }

    public componentWillUnmount() {
        this.destroyed$.next();

        // remove eventListener in case of changing pages
        // (might be another "+" button in other page...)
        // window.removeEventListener('load', () => { });
    }

    private subscribeToImportTypes() {
        importTypes$().pipe(
            takeUntil(this.destroyed$),
        )
        .subscribe(importTypes => {
            this.setState({ importTypes });
        });
    }

    // Le formulaire est fermé par l'epic une fois la création acceptée par l'api,
    // d'où le passage par le store plutôt qu'un état local
    private subscribeToAddImportTypeModal() {
        addImportTypeModal$().pipe(
            takeUntil(this.destroyed$),
        )
        .subscribe(showAddImportTypeDialog => {
            this.setState({ showAddImportTypeDialog });
        });
    }
/*
    private subscribeToFileImporting() {
        fileImporting$().pipe(
            takeUntil(this.destroyed$),
        )
        .subscribe(fileImporting => {
            if()
            this.setState({ importTypes });
        });
    }
*/
    // L'import est un job asynchrone : le total n'est écrit en base qu'à la fin du job,
    // ce bouton est le moyen de le remonter dans la grille sans recharger la page
    private handleOnRefreshTotals = (event: any) => {
        event.preventDefault();

        refreshTotals();
    }

    private handleOnAdd = (event: any) => {
        event.preventDefault();

        setAddImportTypeModal(true);
    }

    // Pas de bounds au premier appel : c'est l'api qui indique où se placer, la carte n'a aucune
    // raison de regarder déjà la bonne zone. Le rechargement par emprise se fait ensuite côté page.
    private handleOnShowOnMap = (event: any, rowData: any) => {
        event.preventDefault();

        getImportLines((rowData as ImportType).id);
    }

    private handleOnClear = (event: any, rowData: any) => {
        event.preventDefault();

        if ( ReadOnlyImportTypeIds.Contains(rowData.id)) {
            alert( `Clear isn't allowed for ${rowData.name} ${rowData.lineType}` );
            return;
        }

        this.setState({ selectedImportType: rowData as ImportType, showClearConfirmationDialog: true });
    }

    private handleOnClearConfirmed = (confirmed: boolean) => {
        this.setState({ showClearConfirmationDialog: false });
        if (!confirmed) return;
        const { selectedImportType } = this.state;
        clearImportType(selectedImportType!.id);
    }

    private handleOnImport = (event: any, rowData: any) => {
        event.preventDefault();
    /*    if ( ReadOnlyImportTypeIds.Contains(rowData.id)) {
            alert( `Import isn't allowed for ${rowData.name} ${rowData.lineType}` );
            return;
        }
        */
        this.setState({selectedImportType: rowData as ImportType, showImportFileDialog: true});
    }

    private handleImportFileDialogClose = (event: any) => {
        this.setState({showImportFileDialog: false});
    }
}
